/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ApiKeyInfoPanel.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Application Keys module.
----------------------------------------------------------------*/


import { Key, Clock } from 'lucide-react';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';

const TTL_PROPS = { position: 'top' as const, maxWidth: '300px' };

export function ApiKeyInfoPanel() {
    const { t } = useI18n();
    const InfoItem = ({ ttKey, labelKey, descKey }: { ttKey: string; labelKey: string; descKey: string }) => (
        <div>� <SimpleTooltip content={t(ttKey)} {...TTL_PROPS}>
            <strong className="underline decoration-dotted">{t(labelKey)}</strong>
        </SimpleTooltip>: {t(descKey)}</div>
    );

    return (
        <>
            {/* Access modes info */}
            <div className="mt-6 pt-6 border-t border-border">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <Key className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium text-blue-900">{t('apiKeys.apiKeyInformation')}</h4>
                            <div className="text-xs text-blue-800 space-y-1">
                                <InfoItem ttKey="apiKeys.tooltips.globalAccess"  labelKey="apiKeys.globalAccess"  descKey="apiKeys.globalAccessDesc" />
                                <InfoItem ttKey="apiKeys.tooltips.tenantSpecific" labelKey="apiKeys.tenantSpecific" descKey="apiKeys.tenantSpecificDesc" />
                                <InfoItem ttKey="apiKeys.tooltips.expiration"    labelKey="apiKeys.expiration"    descKey="apiKeys.expirationDesc" />
                                <InfoItem ttKey="apiKeys.tooltips.security"      labelKey="apiKeys.security"      descKey="apiKeys.securityDesc" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Permissions placeholder */}
            <div className="mt-6 pt-6 border-t border-border">
                <SimpleTooltip content={t('apiKeys.tooltips.permissionsSection')} {...TTL_PROPS}>
                    <label className="block text-sm font-medium text-foreground mb-3">{t('apiKeys.permissions')}</label>
                </SimpleTooltip>
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                    <SimpleTooltip content={t('apiKeys.tooltips.advancedPermissionsNotice')} {...TTL_PROPS}>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>{t('apiKeys.advancedPermissionsNote')}</span>
                        </div>
                    </SimpleTooltip>
                </div>
            </div>
        </>
    );
}
